import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { detect } from 'detect-browser';
import {
  Button,
  RichTextEditorInline,
  CommentActions,
} from '@pwc/pwc-deals-ui';
import {
  CommentTarget,
  CommentType,
  userTokenService,
} from '@pwc/pwc-deals-web-common';
import { richTextFreeForm } from 'common/components/RichTextEditor/CKEditorConfig';
import { getLibraryItemsFromStore } from 'features/contentLibrary/libraryItemService';
import initializeCKEditor from 'common/components/RichTextEditor/initializeCKEditor';
import { useEventHub } from '@pwc/event-hub-ui';
import {
  getImageUploadConfig,
  useCKEditorContext,
} from 'common/contexts/CKEditorContext.tsx';

import { isInlineCommentingEnabled } from '../../../../../helpers/featureFlags';
import styles from './RichTextForm.module.scss';
import { useSyncedInfo } from '../../../../Autosync/SyncedInfoProvider';
import { useRTE } from '../../../SectionContext';
import useCommentsAdapter from '../../../../RichTextEditor/useCummentsAdapter';

/**
 * Component usage: KFA in edit mode
 */
const RichTextForm = ({
  richText,
  setCurrentRichText,
  handleCancel,
  handleSave,
  onBlur,
  onFocus,
  lightModeVariant,
  setEditor,
  libraryItemId,
}) => {
  const { getProjectToken } = userTokenService;
  const { enableImageUpload } = useCKEditorContext();
  const [focused, setFocused] = useState(false);
  const editorContext = useRTE();
  const { projectId } = useParams();
  const projectToken = getProjectToken(projectId);
  const contextStatusInfoItem = useSyncedInfo();
  const { name = '' } = detect();
  const { eventHub } = useEventHub();
  const editorRef = useRef();
  const { currentUser } = useCommentsAdapter();

  return (
    <div
      className={classNames(
        `${
          name === 'safari' ? styles['safari-rich-text-form'] : 'rich-text-form'
        } rich-text-form--outline`,
        {
          'light-mode-variant': lightModeVariant,
        }
      )}
    >
      <RichTextEditorInline
        id={libraryItemId}
        channelId={libraryItemId}
        richTextData={richText}
        onChange={setCurrentRichText}
        config={{
          ...richTextFreeForm,
          ...getImageUploadConfig({
            projectId,
            projectToken,
            richTextFreeForm,
            enableImageUpload,
          }),
        }}
        onBlur={() => {
          setFocused(false);
          if (onBlur) onBlur();
        }}
        onFocus={(_, editor) => {
          setFocused(true);
          editorContext.setEditor(editor);
          if (onFocus) onFocus();
        }}
        isFocused={focused}
        showEmptyRowForTrashIcon
        onInit={(editor) => {
          editorRef.current = editor;
          if (setEditor) {
            setEditor(editor);
          }

          if (!richText) {
            return;
          }

          const { placeholders: childrenData } =
            getLibraryItemsFromStore(richText);

          initializeCKEditor({
            projectId,
            editor,
            richTextData: richText,
            childrenData,
            isReadOnly: false,
            skipAutoSyncOnValidData: true,
            contextStatusInfoItem,
          });
        }}
        onAddCommentThread={(threadId) => {
          const action = {
            type: CommentActions.ADD_COMMENT_THREAD,
            payload: {
              libraryItemId,
              threadId,
              commentTarget: CommentTarget.LibraryItem,
              commentType: CommentType.RichText,
              richTextData: editorRef?.current?.getData(),
            },
          };
          eventHub?.publish('content-add-comment-thread', action);
        }}
        currentUser={currentUser}
        isInlineCommentingEnabled={isInlineCommentingEnabled}
      />
      <div className="edit-button-container">
        {handleCancel && (
          <Button
            data-testid="cancel-button"
            className="button-link mx-2 mr-4"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        )}
        {handleSave && (
          <Button
            data-testid="save-button"
            className="button-block button-small"
            onClick={handleSave}
            disabled={!richText}
          >
            Update
          </Button>
        )}
      </div>
    </div>
  );
};

export default RichTextForm;
